
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

// Interfaces
import { AxiosInstance } from 'axios';
import IFile from '@/ship/Models/IFile';
import FileEntry from '@/plugins/Uploader/FileEntry';
import FileEntryStatus from '@/plugins/Uploader/FileEntryStatus';
import IUploader from '@/plugins/Uploader/IUploader';
import Uploader from '@/plugins/Uploader/Uploader';
import DataTransferExtractor from '@/utils/DataTransferExtractor';
import Http from '@/ship/Http';

// Components
import VFileUploaderEntry from './VFileUploaderEntry.vue';

// Api
import { createLoadViolationsFile } from '@/utils/api/Prescriptions';

@Component({
    components: {
        VFileUploaderEntry,
    },
})
export default class VFileUploaderField extends Vue {
    @Prop({ type: [Array, Object], default: () => ({}) })
    public value!: IFile[];

    @Prop({ type: Boolean, default: false })
    public multiple!: boolean;

    @Prop({ type: Number, default: 10485760 /* 10MB */ })
    public maxSize!: number;

    @Prop({ type: Number, default: 10 })
    public maxFiles!: number;

    @Prop({ type: Number, default: -1 })
    public requiredFiles!: number;

    @Prop({ type: Function, default: Http })
    public instance!: AxiosInstance;

    @Prop({ type: String, default: '/temporary-file' })
    public endpoint!: string;

    @Prop({ type: String, default: 'file' })
    public fieldName!: string;

    @Prop({ type: String, default: '' })
    public accept!: string;

    @Prop({ type: Boolean, default: false })
    public disabled!: boolean;

    @Prop({ type: Boolean, default: false })
    public undeletable!: boolean;

    @Prop({ type: String, default: 'uploader.dragFilesHere' })
    public dragPlaceholder!: string;

    @Prop({ type: String, default: 'uploader.orSelectFiles' })
    public selectPlaceholder!: string;

    @Prop({ type: Boolean, default: false })
    public isAcceptance!: boolean;

    @Prop({ type: Boolean, default: false })
    public forbidden!: boolean;

    @Prop({ type: Boolean, default: false })
    public isClear!: boolean;

    public entries: Array<FileEntry<IFile>> = [];
    public uploader: IUploader<IFile> | null = null;

    @Watch('isClear')
    clearEntries() {
        this.entries = [];
    }

    /**
     * Gets the number of entries.
     */
    public get entiresCount() {
        return this.entries.length;
    }

    /**
     * Gets the number of files required to upload.
     */
    public get requiredFilesCount() {
        return this.requiredFiles - this.entiresCount;
    }

    /**
     * Gets the real max number of files available for upload.
     */
    public get realMaxFiles() {
        return this.requiredFiles > 0 ? this.requiredFiles : this.maxFiles;
    }

    public get allowDownload() {
        return !this.disabled && this.entiresCount < this.realMaxFiles;
    }

    public get allowDownloadMore() {
        return !this.disabled && this.multiple && this.entiresCount < this.realMaxFiles;
    }

    public get isFluidLast() {
        return this.allowDownloadMore ? false : this.entries.length % 2 !== 0;
    }

    public hoverHandler() {
        if (this.$refs.dropzone) {
            const dropzone = this.$refs.dropzone as HTMLElement;

            // Adds a highlight effect.
            dropzone.classList.add('file-uploader--highlight');
            // Removes the leave effect.
            dropzone.classList.remove('file-uploader--leave');
        }
    }

    public leaveHandler() {
        if (this.$refs.dropzone) {
            const dropzone = this.$refs.dropzone as HTMLElement;

            // Sets the leave effect.
            dropzone.classList.add('file-uploader--leave');

            // Waits for 75ms and if the leave class exists
            // it means that the user leaves the dropzone,
            // and we can drop the highlight.
            setTimeout(() => {
                // If the leave effect is exists then drop highlight effect.
                if (dropzone.classList.contains('file-uploader--leave')) {
                    this.dropDropzoneHighlight(dropzone);
                }
            }, 75);
        }
    }

    public dropDropzoneHighlight(dropzone: HTMLElement) {
        dropzone.classList.remove('file-uploader--leave');
        dropzone.classList.remove('file-uploader--highlight');
    }

    public dropHandler(event: DragEvent) {
        event.preventDefault();
        event.stopPropagation();

        if (!event?.dataTransfer) {
            return;
        }

        const extractor = new DataTransferExtractor(event.dataTransfer);

        // Gets all files from event.
        const files = extractor.extractRootFiles();

        this.uploadFiles(files);

        if (this.$refs.dropzone) {
            const dropzone = this.$refs.dropzone as HTMLElement;

            this.dropDropzoneHighlight(dropzone);
        }
    }

    public removeEntry(entry: FileEntry<IFile>) {
        const index = this.entries.indexOf(entry);

        if (index !== -1) {
            this.entries.splice(index, 1);

            this.$emit('entryRemoved', entry, this.entries);
            this.emitInputFiles();
        }
    }

    public uploadHandler() {
        if (this.$refs.input) {
            const input = this.$refs.input as HTMLInputElement;

            const files = input.files!;

            this.uploadFiles(files);

            // Resets the input value.
            input.value = '';
        }
    }

    public async uploadFiles(files: File | File[] | FileList) {
        if (this.entiresCount === this.realMaxFiles) {
            return;
        }

        const entries: FileEntry[] = this.createEntriesFromFiles(files);

        // Removes unnecessary files.
        if (this.entiresCount + entries.length > this.realMaxFiles) {
            // Gets count of files that should be removed.
            // For exemple: 10 - (0 + 12) = -2;
            // 2 items will be removed at the end of the entries.
            // For exemple: 10 - (6 + 8) = -4;
            // 4 items will be removed at the end of the entries.
            const count = this.realMaxFiles - (this.entiresCount + entries.length);

            entries.splice(count);
        }

        // If the multiple is enable then add the elements to the array, otherwise update value.
        if (this.multiple) {
            this.entries.push(...entries);
        } else {
            this.entries = entries;
        }

        if (!entries.length) {
            return;
        }

        if (!this.isAcceptance) {
            this.$emit('uploadingStarted');
            const result = await this.uploader!.upload(entries);
            this.$emit('uploadingEnded', result);
            this.emitInputFiles();
        } else {
            this.$emit('uploadingStarted');
            this.photoUploadHandler(entries[0].file as File);
            entries[0].status = FileEntryStatus.SUCCESSFUL;
        }
    }

    photoUploadHandler(files: File) {
        const formData = new FormData();

        formData.append('file', files);

        createLoadViolationsFile(formData).then((file) => {
            this.$emit('change', file.data.data);
        });
    }

    public createEntriesFromFiles(files: File | File[] | FileList) {
        const entries: FileEntry[] = [];

        if (Array.isArray(files)) {
            for (const file of files) {
                entries.push(new FileEntry(file));
            }
        } else if (files instanceof FileList) {
            for (let index = 0; index < files.length; index++) {
                const file = files.item(index);

                if (file) {
                    entries.push(new FileEntry(file));
                }
            }
        } else {
            const file = files;

            entries.push(new FileEntry(file));
        }

        return entries;
    }

    public emitInputFiles() {
        const data = this.entries
            .filter((entry) => entry.status === FileEntryStatus.SUCCESSFUL)
            .map((entry) => entry.responseModel);

        this.$emit('input', data);
        this.$emit('change', data);
    }

    public showUploadWindow() {
        if (this.$refs.input && this.allowDownload && !this.forbidden) {
            (this.$refs.input as HTMLInputElement).click();
        }
        if (this.forbidden) {
            this.$emit('click');
        }
    }

    public createDropzone() {
        if (this.$refs.dropzone) {
            const dropzone = this.$refs.dropzone as HTMLElement;

            dropzone.addEventListener('drop', this.dropHandler, false);
        }
    }

    public destroyDropzone() {
        if (this.$refs.dropzone) {
            const dropzone = this.$refs.dropzone as HTMLElement;

            dropzone.removeEventListener('drop', this.dropHandler, false);
        }
    }

    public setEntriesFromModel() {
        if (this.value?.length > 0) {
            this.entries = this.value.map((item) => ({
                size: item.size,
                name: item.name,
                type: item.MIMEType,
                progress: 100,
                status: FileEntryStatus.SUCCESSFUL,
                responseModel: item,
            }));
        }
    }

    public isFluid(index: number): boolean {
        return this.isFluidLast && this.entries.length - 1 === index;
    }

    public created() {
        this.uploader = new Uploader<IFile>({
            instance: this.instance,
            accept: this.accept,
            maxSize: this.maxSize,
            endpoint: this.endpoint,
            fieldName: this.fieldName,
        });

        this.setEntriesFromModel();
    }

    public mounted() {
        this.createDropzone();
    }

    public beforeDestroy() {
        this.destroyDropzone();
    }
}
